/**
 * Drag a dataset anywhere over the conversation.
 *
 * Wraps the message area only — not the sidebar and not the workspace panel —
 * so a drop always has one unambiguous meaning.
 *
 * `noClick` and `noKeyboard` are essential: without them every click inside the
 * conversation would open a file picker, and the whole region would become one
 * giant tab stop. The paperclip in the composer is the click affordance.
 *
 * No `accept` filter is set on purpose. Browsers disagree about a zip's MIME
 * type — `application/zip`, `application/x-zip-compressed`, or empty on some
 * platforms — so filtering here would silently refuse valid files with no
 * explanation. The file is validated in `useDatasetBuild.attach`, which can say
 * why.
 *
 * **`multiple` MUST be true, and the handler MUST pass the whole array.** A
 * dropped FOLDER arrives as many files — react-dropzone's file-selector walks
 * the directory for us — so with `multiple: false` the browser hands over a
 * dozen files, react-dropzone rejects the lot as `too-many-files`, and `onDrop`
 * receives an EMPTY accepted list. Dropping a folder then did nothing at all,
 * with nothing on screen to say why. Taking `files[0]` had the same effect from
 * the other end: it threw away every file but one, and a folder minus its
 * contents is not a dataset.
 */
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useI18n } from '../../../i18n';

const AssistantDropZone = ({ onFiles, disabled, children }) => {
  const { t } = useI18n();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      if (files && files.length) onFiles?.(files);
    },
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled,
  });

  return (
    <div {...getRootProps({ className: 'lf-drop' })}>
      <input {...getInputProps()} />
      {children}
      {isDragActive && !disabled && (
        <div className="lf-drop__overlay" aria-hidden="true">
          <div className="lf-drop__card">
            <i className="fa fa-file-archive" />
            <strong>{t('assistant.attach.dropTitle')}</strong>
            <span>{t('assistant.attach.dropBody')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantDropZone;
